import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Accordion from '@repo/ui/accordion'
import CapsuleTab from '@repo/ui/capsule-tab'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { assignCoachCompany } from '../../../../api/centers/assign-coach-company'
import { getCenter } from '../../../../api/centers/get-center'
import { getCenterTripBookings } from '../../../../api/centers/get-center-trip-bookings'
import { getCenterTrips } from '../../../../api/centers/get-center-trips'
import { getOlPermissions } from '../../../../lib/constants/ol-permissions'
import useAuth from '../../../../lib/hooks/use-auth'
import useTranslate from '../../../../lib/hooks/use-translate'
import { TripStepTypeEnum } from '../../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { groupBy } from '../../../../utils/functions/group-by'
import LoadingScreen from '../../../elements/loading/loading-screen'
import TransportPicto from '../../../elements/transport-picto'
import { getTripBookingStepsAsBookings, isEqualData } from '../utils'
import OlCenterTripBookingDetails from './ol-center-trip-booking-details'

export type OlCenterTripMode = 'default' | 'personal-space'

type Props = {
  id: number
  mode?: OlCenterTripMode
  tripBookingReleased?: boolean
}

const OlCenterTabs = ({ id, mode, tripBookingReleased: releasedFromParent }: Props) => {
  const [selectedTrip, setSelectedTrip] = useState<number>()
  const { t } = useTranslate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const { data: center } = useQuery({
    queryKey: ['centers', id],
    queryFn: () => getCenter(id, 'OL_INFORMATION'),
    enabled: !!id && !isNaN(id)
  })

  const olPermissions = getOlPermissions({ role: user?.role, type: center?.ol?.type })
  const tripBookingReleased = releasedFromParent ?? Boolean(center?.tripBookingDiffusedAt)

  const { data: tripBookings } = useQuery({
    queryKey: ['center-trip-bookings', id, 'sort-related-trip-id-asc'],
    queryFn: () => getCenterTripBookings(id, { sort: { relatedTrip: { id: 'asc' } } }),
    enabled: !!id && !isNaN(id)
  })

  const { data: trips } = useQuery({
    queryKey: ['center-trips', id, 'sort-id-asc'],
    queryFn: async () => {
      const res = await getCenterTrips(id, { sort: { id: 'asc' } })
      return res.filter((trip) => !!trip.transportMeans)
    },
    enabled: !!id && !isNaN(id)
  })

  const displayedTrips = useMemo(
    () =>
      trips?.length
        ? trips
        : tripBookings
            ?.map((booking) => booking.relatedTrip)
            .filter((trip, index, self) => self.findIndex(({ id }) => id === trip.id) === index),
    [trips, tripBookings]
  )

  const filteredTrips = useMemo(
    () =>
      displayedTrips?.filter((trip) => {
        // there should be at least one trip booking with this related trip that is locked
        const relatedBookings = tripBookings?.filter(
          (booking) => booking.relatedTrip.id === trip.id
        )
        return relatedBookings ? relatedBookings.some((booking) => booking.locked) : false
      }),
    [displayedTrips, tripBookings]
  )

  const assignCoachCompanyToTrip = useMutation({
    mutationKey: ['assign-coach-company-to-trip'],
    mutationFn: assignCoachCompany,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success(t('coach.company.assigned.successfully'))
      queryClient.invalidateQueries({ queryKey: ['center-trip-bookings', id] })
    }
  })

  useEffect(() => {
    setSelectedTrip((previousSelectedTrip) => {
      if (!filteredTrips?.length) return undefined

      if (
        previousSelectedTrip !== undefined &&
        filteredTrips.some((trip) => trip.id === previousSelectedTrip)
      ) {
        return previousSelectedTrip
      }

      return filteredTrips[0].id
    })
  }, [filteredTrips])

  const filteredTripIds = filteredTrips?.map((trip) => trip.id) ?? []
  const bookingsByTrip =
    tripBookings && filteredTrips
      ? groupBy(
          tripBookings.filter((booking) => filteredTripIds.includes(booking.relatedTrip.id)),
          (booking) => booking.relatedTrip.id
        )
      : []

  if (!tripBookings || !filteredTrips) return <LoadingScreen />

  return (
    <div>
      {filteredTrips.length === 0 && !tripBookingReleased && (
        <InnerBox className="bg-white-hover p-4 rounded-lg">
          <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
            {t('ol.center.trip.booking.data.has.not.been.loaded')}
          </Typography>
        </InnerBox>
      )}
      {filteredTrips.length > 0 && (
        <CapsuleTab
          outline
          className="w-fit p-2"
          variant="secondary"
          activeTab={filteredTrips.findIndex((trip) => trip.id === selectedTrip)}
          onTabChange={(index) => {
            const trip = filteredTrips[index]
            if (trip) {
              setSelectedTrip(trip.id)
              // Scroll to the trip booking details of the selected trip
              const element = document.getElementById(`trip-booking-${trip.id}`)
              if (element) {
                // Scroll slightly up, then smoothly scroll to the element
                window.scrollBy(0, -10) // Adjust the page position slightly
                setTimeout(() => {
                  element.scrollIntoView({ behavior: 'smooth' })
                }, 50)
              }
            }
          }}
          tabs={filteredTrips.map((trip) => ({ label: TripProperties.getTripLabel(trip) }))}
        />
      )}

      <div className="h-3" />
      {Object.entries(bookingsByTrip).map(([tripId, bookings]) => {
        const firstBooking = bookings[0]
        const tripLabel = TripProperties.getTripLabel(firstBooking.relatedTrip)
        const tripPicto = firstBooking.transportMeans?.picto
        // Combine all steps from bookings under this related trip
        const allTrips = getTripBookingStepsAsBookings(bookings)

        return (
          <Accordion
            id={`trip-booking-${tripId}`}
            key={tripId}
            title={
              <div className="flex items-center gap-2">
                {tripPicto && <TransportPicto picto={tripPicto} />}
                <Typography variant="sub-heading" color={tailwindColors.primary.DEFAULT}>
                  {tripLabel}
                </Typography>
              </div>
            }
            open={selectedTrip === Number(tripId)}
            className="bg-white mb-4"
            variant="primary"
            onToggle={() => {
              setSelectedTrip((prev) => (prev === Number(tripId) ? undefined : Number(tripId)))
            }}
          >
            <div className="flex flex-col gap-4">
              {allTrips.map((tripData) => (
                <OlCenterTripBookingDetails
                  olId={olPermissions['edit-coach-company'] ? center?.ol?.id : undefined}
                  key={tripData.tripSteps[0].id}
                  data={tripData}
                  mode={mode}
                  onCoachCompanyChange={async (coachCompanyId, directionality) => {
                    const commonPayload = {
                      centerId: id,
                      arrivalCoachCompanyId:
                        directionality === TripStepTypeEnum.OUTBOUND ? coachCompanyId : undefined,
                      departureCoachCompanyId:
                        directionality === TripStepTypeEnum.INBOUND ? coachCompanyId : undefined
                    }

                    // Find all duplicates (same data except for id)
                    const matchingBookings = bookings.filter((booking) =>
                      isEqualData(booking, tripData)
                    )

                    // Ensure we include the current tripData too, even if not in the bookings list
                    const allToUpdate = [
                      ...new Set([...matchingBookings.map((b) => b.id), tripData.id])
                    ]

                    // Update all matching bookings in parallel
                    await Promise.all(
                      allToUpdate.map((tripBookingId) =>
                        assignCoachCompanyToTrip.mutateAsync({ tripBookingId, ...commonPayload })
                      )
                    )
                  }}
                />
              ))}
            </div>
          </Accordion>
        )
      })}
    </div>
  )
}

export default OlCenterTabs
